import React from 'react';
import { Box, Button, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { ReactComponent as WarningImage } from '../../../assets/delete-img-modal.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from "@mui/material/styles";

type ProjectConfirmationDialogProps = {
	handleDialogClose: (e?: any) => void;
	handleSave: (
		projectData: any,
		) => void;
	projectData: any;
}

type ConfirmationTextResult = {
  confirmationText: string;
  projectNameText: string;
};

const getConfirmationTexts = (
  isUnlink: boolean,
  isQuery: boolean,
  data: any
): ConfirmationTextResult => {
  if (isUnlink) {
    return {
      confirmationText: 'Are you sure to unlink this query?',
      projectNameText: ''
    };
  }

  if (isQuery) {
    return {
      confirmationText: 'Are you sure to delete this query?',
      projectNameText: ''
    };
  }

  return {
    confirmationText: 'Confirm project duplication for',
    projectNameText: `${data?.projectName}.`
  };
};

const getProjectTitleText = (projectData: any): string => {
  const { isUnlink, isQuery, isCost, data } = projectData;

  if (!isUnlink && !isQuery) {
    return 'Confirm Project Duplication';
  }
  if (!data?.queryName) {
    return '';
  }
  // isCost case 
  if (isCost) {
    return data.queryName;
  }
  // isUnlink case
  if (isUnlink) {
    return data.queryName.split('|')[1] || '';
  }
  // isQuery case
  if (data.program === '0') {
    return data.queryName;
  }
  return data.queryName.split('|')[1] || '';
};

const getConfirmationSubText = (projectData: any): string => {
  if (projectData.isUnlink) {
    return 'By confirming, all the changes will be discarded.';
  }
  if (projectData.isQuery) {
    return "Once confirmed, the action can't be undone.";
  }
  return "Do you want to edit the new project's details?";
};

const ProjectConfirmationDialog: React.FC<ProjectConfirmationDialogProps> = ({
	handleSave,
    projectData,
	handleDialogClose
}) => {
	const theme = useTheme();
	const colors = theme.palette;
	// cancel button handler
	const handleCancel = () => {
		handleDialogClose(true);
	};

	const handleClose = () => {
		handleDialogClose(true);
	};

	// Update button handler
	const handleUpdate = () => {
		handleSave(projectData);
	};

	const titleText = getProjectTitleText(projectData);
	const { confirmationText, projectNameText } = getConfirmationTexts(
		projectData.isUnlink,
		projectData.isQuery,
		projectData.data
	);
	const btnLabel = projectData.isQuery ? 'DELETE' : 'CONFIRM';
	const confirmationSubText = getConfirmationSubText(projectData);

	return (
		<>
		<DialogTitle bgcolor="primary.main" sx={{ p: '8px 16px !important' }}>
				<Box display="flex" alignItems="center">
					{projectData.isUnlink && <Typography>
						{'Unlink Query From Project:'}&nbsp;
					</Typography>}
					{projectData.isQuery && <Typography>
						{'Deleting Query:'}&nbsp;
					</Typography>}
					<Typography variant="widgetTitle" flexGrow={1}>
						{titleText}
					</Typography>
					<Box>
						<IconButton onClick={handleClose} sx={{ color: 'white.main' }}>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
		</DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack
			spacing={2}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: `${colors.modal.background} !important`,
				color: `${colors.modal.text} !important`,
				gap: 2,
				px: 2,
				pb: 2,
				pt: 1
			}}
		>
			<Stack direction="row" alignItems="center" spacing={1}>
				<WarningImage className="warning-image" style={{ width: '4.5rem', height: '4.5rem' }} />

				<Stack
					sx={{
						alignItems: 'flex-start',
						justifyContent: 'center',
						pl: '1rem'
					}}
				>
					<Typography sx={{ textAlign: 'left', pb: '0.5rem', fontWeight:'500', fontSize: '1.25rem'}} >
						{confirmationText}{' '} 
						<Box component="span" sx={{fontWeight: '700 !important', fontSize: '1.25rem'}}>
							{projectNameText}
						</Box>
					</Typography>
					<Typography sx={{ textAlign: 'left', color: '#71758F' }}>
						{confirmationSubText}
					</Typography>
				</Stack>
			</Stack>
			<Stack direction="row" justifyContent="space-between" gap={2} sx={{ alignSelf: 'flex-end' }}>
					<Button variant="pepwiseOutline" sx={{ width: '10rem', color: 'info.main' }} onClick={handleCancel}>
						NO
					</Button>
					<Button
						variant={projectData.isUnlink || projectData.isQuery ? "pepwiseError" : 'pepwiseSecondary' }
						sx={{ width: '10rem' }}
						onClick={handleUpdate}
					>
						{projectData.isUnlink ? 'UNLINK' : btnLabel }
					</Button>
			</Stack>
		</Stack>
      </DialogContent>
	  </>
	);
};

export default ProjectConfirmationDialog;
